import { NAV_PATHS } from '@modules/common/constants';
import {
  DataIngestionInProgressIndicator,
  DisabledIntegrationIndicator,
} from '@modules/integrationsV2/components/IntegrationStateIndicator';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import { IntegrationSource } from '@modules/integrationsV2/types';
import {
  getDisplayNameForIntegrationType,
  getIconForIntegrationType,
  trackIntegrationPageEvent,
} from '@modules/integrationsV2/utils';
import { ExpandMoreRounded, WarningAmberRounded } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { IntegrationType } from 'generated/graphql';
import { Link } from 'react-router-dom';

interface IntegrationSourceListByTypeProps {
  selected: IntegrationSource | undefined;
  integrationSourcesGroupedByType: Record<IntegrationType, IntegrationSource[]>;
  integrationType: IntegrationType;
}

export const IntegrationSourceListByType = ({
  selected,
  integrationSourcesGroupedByType,
  integrationType,
}: IntegrationSourceListByTypeProps) => {
  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const onSelect = (integrationSource: IntegrationSource) => {
    dispatch({
      type: IntegrationsActionType.SET_VIEW,
      payload: {
        selectedIntegrationSourceOption: {
          integrationType: integrationSource.integrationType,
          channelType: integrationSource.channel.type,
          integrationID: integrationSource.integrationID,
          namespace: integrationSource.namespace,
        },
      },
    });
    trackIntegrationPageEvent({
      eventName: 'Integrations - Selected Integration Source From List',
      integration: {
        integrationType: integrationSource.integrationType,
        channelType: integrationSource.channel.type,
        namespace: integrationSource.namespace,
      },
    });
  };

  const renderListItem = (integrationSource: IntegrationSource) => {
    const {
      channel,
      integrationType,
      namespace,
      isDisabled,
      isIngestionInProgress,
      isIncompleteIntegration,
      eqlFieldsGroupID,
    } = integrationSource;

    const isSelected =
      selected?.integrationType === integrationType &&
      selected?.channel.type === channel.type &&
      selected?.namespace === namespace;

    const integrationSourceID = `${eqlFieldsGroupID}+${integrationType}`;

    return (
      <ListItem dense disablePadding key={integrationSourceID}>
        <ListItemButton
          onClick={() => onSelect(integrationSource)}
          selected={isSelected}
          component={Link}
          to={`/${NAV_PATHS.SETTINGS_INTEGRATIONS}/${encodeURIComponent(
            integrationSourceID,
          )}`}
          sx={{
            pl: 4.4,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: '36px',
            }}
          >
            <Box
              width="24px"
              component="img"
              alignSelf="center"
              src={channel.logo}
              alt={channel.displayName}
            />
          </ListItemIcon>
          <ListItemText
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            primary={channel.displayName}
            secondary={
              namespace ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {namespace}
                </Typography>
              ) : null
            }
          />
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {isDisabled && <DisabledIntegrationIndicator variant="icon" />}
            {isIngestionInProgress && (
              <DataIngestionInProgressIndicator variant="icon" />
            )}
            {isIncompleteIntegration && (
              <Tooltip arrow title="Action required" placement="top">
                <WarningAmberRounded color="warning" />
              </Tooltip>
            )}
          </Stack>
        </ListItemButton>
      </ListItem>
    );
  };

  const integrationSources =
    integrationSourcesGroupedByType[integrationType] ?? [];

  const activeIntegrationSources = integrationSources.filter(
    (integrationSource) => !integrationSource.isDisabled,
  );

  const disabledIntegrationSources = integrationSources.filter(
    (integrationSource) => integrationSource.isDisabled,
  );

  const hasActiveIntegrationSources = activeIntegrationSources.length > 0;

  const shouldShowDisabledIntegrationSourcesAccordion =
    hasActiveIntegrationSources && disabledIntegrationSources.length > 0;

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {getIconForIntegrationType({ integrationType })}
        <Typography>
          {getDisplayNameForIntegrationType(integrationType)}
        </Typography>
      </Stack>
      {integrationSources.length > 0 ? (
        <Stack>
          <List disablePadding dense>
            {hasActiveIntegrationSources
              ? activeIntegrationSources.map((integrationSource) =>
                  renderListItem(integrationSource),
                )
              : disabledIntegrationSources.map((integrationSource) =>
                  renderListItem(integrationSource),
                )}
          </List>
          {shouldShowDisabledIntegrationSourcesAccordion && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: 'transparent',
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreRounded
                    sx={{
                      color: 'text.secondary',
                    }}
                  />
                }
                sx={{
                  p: 0,
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    mr: '16px',
                  },
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    pl: '36px',
                  }}
                >
                  Disabled ({disabledIntegrationSources.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  p: 0,
                }}
              >
                <List disablePadding dense>
                  {disabledIntegrationSources.map((integrationSource) =>
                    renderListItem(integrationSource),
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      ) : (
        <Typography color="text.disabled" pl={4.5}>
          No integrations
        </Typography>
      )}
    </Stack>
  );
};
